import '@app/lib/i18n';
import React, {lazy, Suspense, useMemo} from 'react';
import {createRoot} from 'react-dom/client';
import {BrowserRouter, useRoutes} from 'react-router-dom';
import type {PageRoute} from 'virtual:modules';
import '@app/assets/css/index.css';
import LoadingScreen from '@app/components/LoadingScreen';
import {AuthProvider} from '@modules/auth/hooks/context/AuthContext';
import {BreadcrumbsContextProvider} from '@app/hooks/context/BreadcrumbsContext';
import {QueryClientProvider} from "@tanstack/react-query";
import {queryClient} from '@app/lib/query-client';
import NotFound from '@app/components/errors/404';
import { ConfirmationProvider } from "react-confirmation-box";
import "react-confirmation-box/style.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AuthenticatedAppFrame from '@app/layouts/authenticated-app-frame';


// dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const {pages} = await import("virtual:modules");

const transformRoutes = (routes: PageRoute[]): any[] => {
    return routes.map(route => {
        const Component = route.component ? lazy(route.component) : null;

        return {
            path: route.path,
            index: route.index,
            element: Component ? (
                <Suspense fallback={<LoadingScreen fullscreen={false} />}>
                    <Component />
                </Suspense>
            ) : undefined,
            children: route.children ? transformRoutes(route.children) : undefined
        };
    });
};

const AppRoutes = () => {
    const routes = useMemo(() => [...transformRoutes(pages), { path: "*", element: <NotFound /> }], []);
    return useRoutes(routes);
};

const App = () => (
    <BrowserRouter>
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <BreadcrumbsContextProvider>
                    <AuthenticatedAppFrame>
                        <AppRoutes />
                    </AuthenticatedAppFrame>
                </BreadcrumbsContextProvider>
            </AuthProvider>
        </QueryClientProvider>
        <ConfirmationProvider />
    </BrowserRouter>
);

const container = document.getElementById('root');

if (!container) {
    throw new Error('Root container #root not found');
}

const root = import.meta.hot?.data.root ?? (() => {
    container.innerHTML = "";
    return createRoot(container);
})();

if (import.meta.hot) {
    import.meta.hot.data.root = root;
}

root.render(<App/>);
